var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { ResizeObserver } from '@juggle/resize-observer';
import { useConsoleLog } from '@hawkins/helpers';
import { HawkinsGlobalClass } from '@hawkins/styles';
import { Tooltip } from './tooltip';
var displayName = 'EllipsisTooltip';
/**
 * Tooltip that will only appear when there is an ellipsis text truncation.
 * You need to pass the same ref to both the EllipsisTooltip and the immediate child
 * that gets truncated. This is how EllipsisTooltip determines
 * if the child got truncated and thus needs to show. See the source of the demo for an example.
 */
export var EllipsisTooltip = React.forwardRef(function (props, ref) {
    var label = props.label, children = props.children, restProps = __rest(props, ["label", "children"]);
    var _a = __read(React.useState(false), 2), hasEllipsis = _a[0], setHasEllipsis = _a[1];
    var _b = __read(React.useState(ref && ref.current), 2), container = _b[0], setContainer = _b[1];
    var containerRef = React.useRef(null);
    var isMountedRef = React.useRef(false);
    useConsoleLog(typeof children === 'function' ? false : !ref, 'A valid ref is required', {
        displayName: displayName,
        level: 'error',
    });
    React.useEffect(function () {
        var ro = new ResizeObserver(function (entries) {
            var e_1, _a;
            var _loop_1 = function (entry) {
                var hasXOverflow = entry.target.scrollWidth > entry.target.clientWidth;
                var hasYOverflow = entry.target.scrollHeight > entry.target.clientHeight;
                window.requestAnimationFrame(function () {
                    if (isMountedRef.current) {
                        setHasEllipsis(hasXOverflow || hasYOverflow);
                    }
                });
            };
            try {
                for (var entries_1 = __values(entries), entries_1_1 = entries_1.next(); !entries_1_1.done; entries_1_1 = entries_1.next()) {
                    var entry = entries_1_1.value;
                    _loop_1(entry);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (entries_1_1 && !entries_1_1.done && (_a = entries_1.return)) _a.call(entries_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
        });
        isMountedRef.current = true;
        if (container) {
            ro.observe(container);
        }
        return function () {
            isMountedRef.current = false;
            if (container) {
                ro.unobserve(container);
            }
        };
    }, [container, label]);
    React.useEffect(function () {
        var currentContainer = ref && ref.current;
        // Quick check for inner containers that contain the truncation
        // This will only grab the first element that matches, useful for Badge, Chip, Tag, TextLink
        var innerContainer = currentContainer === null || currentContainer === void 0 ? void 0 : currentContainer.querySelector(".".concat(HawkinsGlobalClass.TRUNCATE));
        setContainer(innerContainer || currentContainer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, ref && ref.current]);
    React.useEffect(function () {
        if (containerRef && containerRef.current) {
            var currentContainer = containerRef && containerRef.current;
            // Quick check for inner containers that contain the truncation
            // This will only grab the first element that matches, useful for Badge, Chip, Tag, TextLink
            var innerContainer = currentContainer === null || currentContainer === void 0 ? void 0 : currentContainer.querySelector(".".concat(HawkinsGlobalClass.TRUNCATE));
            setContainer(innerContainer || currentContainer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerRef, containerRef && containerRef.current]);
    var childContent = typeof children === 'function' ? children(containerRef) : children;
    return (_jsx(Tooltip, __assign({}, restProps, { label: label, disabled: !hasEllipsis }, { children: childContent })));
});
EllipsisTooltip.displayName = displayName;
